/**
 * One-at-a-time card audition helper for Music/SFX previews and UI cues.
 * Stops/disposes prior audio on switch; swallows autoplay/decode failures.
 */

export type AuditionOptions = {
  volume?: number;
  /** When true, skip playback (reduced-motion / muted preference). */
  muted?: boolean;
};

export type AuditionFailure = {
  url: string;
  reason: string;
};

function disposeElement(el: HTMLAudioElement): void {
  try {
    el.pause();
  } catch {
    /* ignore */
  }
  try {
    el.removeAttribute("src");
    // load() resets the element pipeline after src removal when supported
    el.load();
  } catch {
    /* ignore */
  }
}

export class AudioAuditionController {
  private current: HTMLAudioElement | null = null;
  private generation = 0;
  private lastFailure: AuditionFailure | null = null;
  private readonly onEnded = (ev: Event) => {
    const el = ev.currentTarget as HTMLAudioElement;
    this.cleanupIfCurrent(el);
  };
  private readonly onError = (ev: Event) => {
    const el = ev.currentTarget as HTMLAudioElement;
    this.lastFailure = {
      url: el.currentSrc || el.src || "",
      reason: "decode-or-network",
    };
    this.cleanupIfCurrent(el);
  };

  getLastFailureForTests(): AuditionFailure | null {
    return this.lastFailure;
  }

  getCurrentForTests(): HTMLAudioElement | null {
    return this.current;
  }

  private detach(el: HTMLAudioElement): void {
    el.removeEventListener("ended", this.onEnded);
    el.removeEventListener("error", this.onError);
  }

  private cleanupIfCurrent(el: HTMLAudioElement): void {
    if (this.current !== el) return;
    this.detach(el);
    disposeElement(el);
    this.current = null;
  }

  /** Stop and dispose any in-flight audition. */
  stop(): void {
    this.generation += 1;
    const el = this.current;
    this.current = null;
    if (!el) return;
    this.detach(el);
    disposeElement(el);
  }

  /**
   * Play a short one-shot. Replaces any prior audition.
   * Never rejects — autoplay/decode errors are caught.
   */
  play(url: string, opts: AuditionOptions = {}): void {
    if (opts.muted) {
      this.stop();
      return;
    }
    this.stop();
    const gen = this.generation;
    let el: HTMLAudioElement;
    try {
      el = new Audio(url);
    } catch (err) {
      this.lastFailure = {
        url,
        reason: err instanceof Error ? err.message : "construct-failed",
      };
      return;
    }
    el.preload = "auto";
    el.volume = Math.min(1, Math.max(0, opts.volume ?? 0.55));
    this.current = el;
    el.addEventListener("ended", this.onEnded);
    el.addEventListener("error", this.onError);
    void el.play().catch((err) => {
      this.lastFailure = {
        url,
        reason: err instanceof Error ? err.message : "autoplay-blocked",
      };
      // Only dispose if this element is still the active audition for this generation.
      if (this.generation === gen && this.current === el) {
        this.cleanupIfCurrent(el);
      }
    });
  }

  dispose(): void {
    this.stop();
  }
}

/** True when the user prefers reduced motion (also used as a soft sound gate). */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
